import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/modules/auth/shared/auth.service';
import { LocalStoreService } from '../../services/local-store.service';
import { RealtimeDatabaseService } from '../../services/realtime-database.service';
import { ToasterNotificationService } from '../../services/toaster-notification.service';
import { BellToasterNotificationService } from '../../services/bell-toaster-notification.service';
import { LoginPopupComponent } from '../login-popup/login-popup.component';
import { CryptoDepositComponent } from '../crypto-deposit/crypto-deposit.component';
import { TranslationService } from 'src/app/modules/core/services/translation.service';
import { SupabaseService } from '../../services/supabase.service';
import { RealTimeBalanceResponse } from 'src/types/balance';
import { Store } from '@ngrx/store';
import { selectUserDetailsData } from 'src/app/store/user-details/user-details.selectors';
import { setBalances } from 'src/app/store/user-details/user-details.actions';
import { ProvidersVisibilityService } from 'src/app/shared/services/providers-visibility.service';
import { VirtualsVerdictRefreshService } from 'src/app/shared/services/virtuals-verdict-refresh.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  providers: [MessageService, DialogService],
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Output() toggleLeftSidenav = new EventEmitter();
  @Output() toggleRightSidenav = new EventEmitter();

  @Input() userBalance;
  @Input() username;

  classes: any;
  fluid = true;
  supabaseBalanceSubscription: Subscription;
  public balance: number;
  public timer: any;

  private userBalanceSubId: string;

  openedLeft = false;
  openedRight = false;
  ref: DynamicDialogRef;
  uid;
  isLogin = false;
  // Virtuals lobby entry: the two kill switches AND the server's per-account
  // verdict on GET /user/getUserAccount, for the signed-in account only —
  // exactly what ProvidersEnabledGuard enforces on the route. No network call
  // of its own (ProvidersVisibilityService).
  canSeeVirtuals$ = this.providersVisibility.canSeeVirtuals$;
  // Deposits are strictly for self-registered accounts. Clients created by a
  // branch or super admin are funded through their branch, so the quick-deposit
  // button must not exist for them — hiding it on one layout only would leave
  // the door open on the other.
  canQuickDeposit = false;
  private readonly isBrowser: boolean;
  private depositModalPrefetched = false;
  // The header is destroyed with the layout (around /auth and /maintenance);
  // its long-lived subscriptions end with it.
  private readonly destroy$ = new Subject<void>();

  @ViewChild('myDrop', { static: true }) myDrop: NgbDropdown;

  constructor(
    public router: Router,
    public authService: AuthService,
    public dialogService: DialogService,
    private messageService: MessageService,
    private rdService: RealtimeDatabaseService,
    private store: Store,
    private supabase: SupabaseService,
    private localStorageService: LocalStoreService,
    private translateService: TranslateService,
    private toasterNotificationService: ToasterNotificationService,
    private bellToasterNotificationService: BellToasterNotificationService,
    private translationService: TranslationService,
    private providersVisibility: ProvidersVisibilityService,
    private verdictRefresh: VirtualsVerdictRefreshService,
    @Inject(PLATFORM_ID) private platformId: string
  ) {
    this.isBrowser = isPlatformBrowser(platformId);

    authService.event.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.username = data;
    });
    // Login başarılı olduğunda getUserAccount'u çağırmak için event'i dinle
    if (this.isBrowser) {
      authService.userAccountRefresh$.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.getUserAccount();
      });
      this.getUserAccount();
    }
  }

  ngOnInit(): void {
    // ONE store subscription for the life of the header. It used to be opened
    // inside getUserAccount(), i.e. once more on every login refresh, and
    // never closed.
    this.store
      .select(selectUserDetailsData)
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.canQuickDeposit = !!user && user.role === 'clientRegistered';
        if (user && user.balance !== undefined) {
          if (user.balance && user.balance > 0) {
            this.balance = user.balance;
          } else {
            this.balance = 0;
          }
        }
      });

    this.authService.currentAuthStatus.pipe(takeUntil(this.destroy$)).subscribe((authStatus) => {
      if (authStatus) {
        this.isLogin = true;
        //every 1 hour supabase token will be expired
        if (this.isBrowser) {
          // Only for signed-in users: they are the ones who can deposit, and
          // this is exactly when the (uncached, account-bundled) modal chunk
          // is worth warming.
          this.prefetchDepositModal();
          this.startListening();
          this.timer = setInterval(() => {
            this.startListening();
          }, 60 * 60 * 1000);
        }
      } else {
        this.isLogin = false;
        this.stopListening();
        this.timer && clearInterval(this.timer);
      }
    });
  }

  ngAfterViewInit() {
    this.toasterNotificationService.receiveMessage((notification) => {
      const currentLanguage = this.translationService.getSelectedLanguage();
      const data = notification.notification[currentLanguage];
      const title = data.title;
      const body = data.body.replace('&#x2F;', '/');
      const templateType = notification.template.type;

      this.messageService.add({
        key: 'generic-toaster',
        severity: templateType,
        summary: title,
        detail: body,
        life: 30000,
      });
    });

    this.bellToasterNotificationService.receiveMessage((notification) => {
      const currentLanguage = this.translationService.getSelectedLanguage();
      const data = notification.notification[currentLanguage];
      const title = data.title;
      const bodyHtml = data.bodyHtml.replace('&#x2F;', '/');
      const body = data.body;
      const templateType = notification.template.name;

      this.messageService.add({
        severity: 'custom',
        key: 'bell-toaster',
        summary: title,
        detail: bodyHtml,
        life: 30000,
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    clearInterval(this.timer);
    this.stopListening();
  }

  closeDropdown(): any {
    this.myDrop.close();
  }

  toggleSidebar(isLeft): void {
    isLeft ? this.toggleLeftSidenav.emit() : this.toggleRightSidenav.emit();
  }

  login() {
    this.ref = this.dialogService.open(LoginPopupComponent, {
      showHeader: true,
      dismissableMask: true,
      closable: true,
      styleClass: 'login-modal',
      contentStyle: { 'border-radius': '4px', padding: '0', 'z-index': '999' },
    });
  }

  // Quick deposit from the header — opens the unified picker (TND methods +
  // Crypto card). The TND payment module is lazy (bundled with the account
  // area), so the modal component is imported on demand rather than dragging
  // the whole module into the eager header bundle.
  // Warm the lazy deposit-modal chunk BEFORE the user taps. It is bundled
  // with the account area, so a brand-new one-click user has never fetched
  // it — and quickDeposit() below only opens the dialog inside .then(), so
  // until the chunk lands there is no modal and therefore no spinner: the
  // tap looks like nothing happened. Prefetching on idle overlaps that
  // download with the user reading the page. Safe to call repeatedly:
  // webpack de-duplicates, and the chunk is served immutable.
  prefetchDepositModal(): void {
    if (!this.isBrowser || this.depositModalPrefetched) {
      return;
    }
    this.depositModalPrefetched = true;
    const warm = () =>
      import(
        'src/app/modules/tnd-payment/components/tnd-deposit-modal/tnd-deposit-modal.component'
      ).catch(() => {
        // Prefetch is best-effort: a failure here must never surface — the
        // real import in quickDeposit() will retry and handle errors.
        this.depositModalPrefetched = false;
      });
    const w = window as any;
    if (typeof w.requestIdleCallback === 'function') {
      w.requestIdleCallback(warm, { timeout: 3000 });
    } else {
      setTimeout(warm, 1200);
    }
  }

  quickDeposit(): void {
    // Belt and braces with the template *ngIf: nothing else may open the
    // deposit modal for a branch-created account.
    if (!this.isBrowser || !this.canQuickDeposit) {
      return;
    }
    import(
      'src/app/modules/tnd-payment/components/tnd-deposit-modal/tnd-deposit-modal.component'
    ).then(({ TndDepositModalComponent }) => {
      const depositRef = this.dialogService.open(TndDepositModalComponent, {
        showHeader: false,
        dismissableMask: true,
        width:
          typeof window !== 'undefined' && window.innerWidth > 768
            ? '560px'
            : '95vw',
        contentStyle: { 'max-height': '92vh', overflow: 'auto', padding: '0' },
        baseZIndex: 10000,
        styleClass: 'tnd-payment-modal',
      });
      // Same crypto fall-through contract as the wallet page picker.
      depositRef.onClose.subscribe((result) => {
        if (result && result.openCrypto) {
          this.dialogService.open(CryptoDepositComponent, {
            data: { status: 'status' },
            showHeader: true,
            dismissableMask: true,
            width: '50vw',
            contentStyle: { 'min-height': '65vh', padding: '0' },
            baseZIndex: 10000,
          });
        }
      });
    });
  }

  logOut() {
    this.authService.logOut();
    this.isLogin = false;
  }

  // Through the shared single-flight account request, which dispatches the
  // payload into the store itself: at boot the layout asks for the same
  // account in the same tick, and both now ride one call.
  //
  // Subscribed only to send the request. this.balance comes from ngOnInit's
  // store subscription, never from the payload handed back here: every caller
  // receives that payload even when the service REFUSED to store it (an
  // answer for an account that has since logged out or been replaced), so
  // reading it would show that other account's balance.
  getUserAccount(): void {
    if (!this.isBrowser) {
      return;
    }
    this.verdictRefresh
      .refreshAccount()
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  async startListening() {
    if (!this.isBrowser) {
      return;
    }
    this.supabaseBalanceSubscription = await this.supabase.getUserBalance(
      this.readBalance.bind(this)
    );
  }

  stopListening(): void {
    if (this.supabaseBalanceSubscription) {
      this.supabaseBalanceSubscription.unsubscribe();
    }
  }

  readBalance(data: RealTimeBalanceResponse): void {
    if (data.new) {
      this.balance = data.new.balance || 0;
      // This realtime feed carries the raw wallet row, which bypasses the API's capping. The
      // stored withdrawable figure can exceed the balance while a bet is open, so cap it here
      // too or the store would be overwritten with a number no rail will actually pay.
      this.store.dispatch(setBalances({
        balance: data.new.balance || 0,
        availableWithdrawalAmount: Math.min(
          data.new.available_withdrawal_amount || 0,
          data.new.balance || 0
        )
      }));

      if (
        this.balance &&
        data.new.balance > this.balance &&
        data.new.notification
      ) {
        this.messageService.clear();
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          life: 30000,  
          detail: this.translateService.instant(
            'ALERT.MONEY_HAS_BEEN_DEPOSITED'
          ),
        });
      }
    }
  }
}
